import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

/**
 * Web Vitals Performance Monitor for Lighthouse 100/100
 * Tracks Core Web Vitals and sends analytics
 */

const sendToAnalytics = (metric) => {
  // Send to Google Analytics 4
  if (window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
      custom_map: {
        metric_id: metric.id,
        metric_value: metric.value,
        metric_delta: metric.delta,
      },
    });
  }

  // Send to console for development
  if (process.env.NODE_ENV === 'development') {
    console.log(`${metric.name}:`, metric);
  }

  // Send to custom analytics endpoint (optional)
  if (process.env.REACT_APP_ANALYTICS_ENDPOINT) {
    fetch(process.env.REACT_APP_ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: metric.name,
        value: metric.value,
        id: metric.id,
        delta: metric.delta,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      }),
    }).catch(console.error);
  }
};

const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    // Core Web Vitals
    getCLS(sendToAnalytics);  // Cumulative Layout Shift
    getFID(sendToAnalytics);  // First Input Delay
    getFCP(sendToAnalytics);  // First Contentful Paint
    getLCP(sendToAnalytics);  // Largest Contentful Paint
    getTTFB(sendToAnalytics); // Time to First Byte
    
    // Also call the provided callback
    getCLS(onPerfEntry);
    getFID(onPerfEntry);
    getFCP(onPerfEntry);
    getLCP(onPerfEntry);
    getTTFB(onPerfEntry);
  }
};

/**
 * Performance Budget Monitor
 * Warns if metrics exceed thresholds for Lighthouse 100/100
 */
export const performanceBudget = {
  // Core Web Vitals thresholds for "Good" rating
  LCP: 2500,  // Largest Contentful Paint (ms)
  FID: 100,   // First Input Delay (ms)
  CLS: 0.1,   // Cumulative Layout Shift
  FCP: 1800,  // First Contentful Paint (ms)
  TTFB: 800,  // Time to First Byte (ms)
};

export const checkPerformanceBudget = (metric) => {
  const threshold = performanceBudget[metric.name];
  if (!threshold) return;

  const isGood = metric.value <= threshold;
  const status = isGood ? '✅' : '❌';
  
  console.log(
    `${status} ${metric.name}: ${metric.value}${metric.name === 'CLS' ? '' : 'ms'} ` +
    `(Budget: ${threshold}${metric.name === 'CLS' ? '' : 'ms'})`
  );

  if (!isGood) {
    console.warn(
      `Performance Budget Exceeded: ${metric.name} is ${metric.value}${metric.name === 'CLS' ? '' : 'ms'}, ` +
      `which exceeds the budget of ${threshold}${metric.name === 'CLS' ? '' : 'ms'}`
    );
  }

  return isGood;
};

/**
 * Initialize performance monitoring
 */
export const initPerformanceMonitoring = () => {
  // Monitor Core Web Vitals with budget checking
  getCLS((metric) => {
    sendToAnalytics(metric);
    checkPerformanceBudget(metric);
  });

  getFID((metric) => {
    sendToAnalytics(metric);
    checkPerformanceBudget(metric);
  });

  getFCP((metric) => {
    sendToAnalytics(metric);
    checkPerformanceBudget(metric);
  });

  getLCP((metric) => {
    sendToAnalytics(metric);
    checkPerformanceBudget(metric);
  });

  getTTFB((metric) => {
    sendToAnalytics(metric);
    checkPerformanceBudget(metric);
  });

  // Monitor Resource Loading Performance
  if ('PerformanceObserver' in window) {
    // Monitor Long Tasks (for better FID)
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            console.warn('Long Task detected:', {
              duration: entry.duration,
              startTime: entry.startTime,
              name: entry.name,
            });
          }
        }
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      // Long tasks not supported in this browser
    }

    // Monitor Layout Shifts in detail
    try {
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            console.log('Layout Shift detected:', {
              value: entry.value,
              sources: entry.sources?.map(source => ({
                element: source.node,
                currentRect: source.currentRect,
                previousRect: source.previousRect,
              })),
            });
          }
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      // Layout shift observation not supported
    }

    // Monitor Largest Contentful Paint candidates
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        console.log('LCP Candidate:', {
          element: lastEntry.element,
          startTime: lastEntry.startTime,
          size: lastEntry.size,
          url: lastEntry.url,
        });
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // LCP observation not supported
    }
  }

  // Monitor JavaScript Heap Usage
  if ('memory' in performance) {
    setInterval(() => {
      const memInfo = performance.memory;
      const usedPercent = (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100;
      
      if (usedPercent > 80) {
        console.warn('High memory usage detected:', {
          used: Math.round(memInfo.usedJSHeapSize / 1048576) + ' MB',
          total: Math.round(memInfo.jsHeapSizeLimit / 1048576) + ' MB',
          percentage: Math.round(usedPercent) + '%',
        });
      }
    }, 30000); // Check every 30 seconds
  }
};

/**
 * Network Information Monitoring
 */
export const monitorNetworkInfo = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    
    console.log('Network Info:', {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
    });

    // Adjust quality based on network
    if (connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      document.documentElement.classList.add('low-bandwidth');
      console.log('Low bandwidth detected, applying optimizations');
    }

    connection.addEventListener('change', () => {
      console.log('Network conditions changed:', {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
      });
    });
  }
};

/**
 * Lighthouse Performance Score Simulator
 */
export const estimateLighthouseScore = () => {
  // This is a rough estimation based on Core Web Vitals
  // Actual Lighthouse uses more complex scoring
  
  const metrics = {};
  
  return new Promise((resolve) => {
    let collected = 0;
    const total = 5;
    
    const checkComplete = () => {
      collected++;
      if (collected === total) {
        const score = calculateEstimatedScore(metrics);
        resolve(score);
      }
    };

    getCLS((metric) => { metrics.CLS = metric.value; checkComplete(); });
    getFID((metric) => { metrics.FID = metric.value; checkComplete(); });
    getFCP((metric) => { metrics.FCP = metric.value; checkComplete(); });
    getLCP((metric) => { metrics.LCP = metric.value; checkComplete(); });
    getTTFB((metric) => { metrics.TTFB = metric.value; checkComplete(); });
  });
};

const calculateEstimatedScore = (metrics) => {
  let score = 100;
  
  // Deduct points based on thresholds
  if (metrics.LCP > 2500) score -= 20;
  else if (metrics.LCP > 4000) score -= 40;
  
  if (metrics.FID > 100) score -= 15;
  else if (metrics.FID > 300) score -= 30;
  
  if (metrics.CLS > 0.1) score -= 15;
  else if (metrics.CLS > 0.25) score -= 30;
  
  if (metrics.FCP > 1800) score -= 10;
  else if (metrics.FCP > 3000) score -= 20;
  
  if (metrics.TTFB > 800) score -= 10;
  else if (metrics.TTFB > 1800) score -= 20;
  
  return Math.max(0, score);
};

export default reportWebVitals;